import React, { useEffect, useState, useCallback } from "react";
import { Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Banner from "../pages/auth/Banner";
import "bootstrap/dist/css/bootstrap.min.css";
import "../pages/auth/index.css";
import { setUserToken } from "@/helpers";
import {
  runWelnetAuthentication,
  shouldRunWelnetAuthentication,
} from "@/helpers/welnetAuth";
import { useDispatch } from "react-redux";
import { clearStoragePreservingNeffyUse } from "@/helpers/neffyUse";
import { getOAuthData } from "@/services";

const parseCallbackUser = (userParam) => {
  if (!userParam) {
    return null;
  }

  try {
    return JSON.parse(userParam);
  } catch (error) {
    return null;
  }
};

const SocialCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const params = new URLSearchParams(location.search);
  const success = params.get("success");
  const user = parseCallbackUser(params.get("user"));
  const accessToken = params.get("accessToken");
  const expiresAt = params.get("expiresAt");
  const getMessage = params.get("error") || params.get("message");
  const redirectURL = params.get("redirect_uri");
  const refreshToken = params.get("refreshToken");
  const key = params.get("key");

  const handleUpdateLogin = useCallback(() => {
    setUserToken({ accessToken, expiresAt, user, refreshToken });
    window.location.replace("/dashboard");
    setIsLoading(false);
  }, [accessToken, expiresAt, user, refreshToken]);

  const clearLocalStorageAndNavigate = (url) => {
    clearStoragePreservingNeffyUse();
    window.location.assign(url);
  };

  // onCompleteOverride lets callers who already set the correct token via a
  // different source (e.g. the key-exchange branch below, whose real token
  // lives in a local `oauthAccessToken`/`oauthUser`, not in this component's
  // accessToken/user which are parsed straight from the URL and are null for
  // that flow) skip handleUpdateLogin's redundant/incorrect setUserToken call.
  const handleWelnetAuthentication = useCallback(
    (authenticatedUser, onCompleteOverride) => {
      runWelnetAuthentication({
        dispatch,
        email: authenticatedUser?.email,
        onComplete: onCompleteOverride || handleUpdateLogin,
        onExternalRedirect: clearLocalStorageAndNavigate,
      })
        .catch(() => {
          setIsLoading(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, handleUpdateLogin],
  );

  const completeSocialLogin = useCallback(
    (authenticatedUser) => {
      setUserToken({ accessToken, expiresAt, user, refreshToken });

      if (shouldRunWelnetAuthentication(authenticatedUser)) {
        handleWelnetAuthentication(authenticatedUser);
        return;
      }

      handleUpdateLogin();
    },
    [accessToken, expiresAt, user, refreshToken, handleUpdateLogin, handleWelnetAuthentication],
  );

  useEffect(() => {
    setIsLoading(true);

    if (success === "0") {
      toast.error(getMessage || "Social Login failed. Please try again.");
      navigate("/");
      setIsLoading(false);
      return;
    }

    if (key) {
      dispatch(getOAuthData(key)).then((res) => {
        if (res?.payload?.success) {
          const {
            accessToken: oauthAccessToken,
            user: oauthUser,
            redirect_uri,
            expiresAt: oauthExpiresAt,
            refreshToken: oauthRefreshToken,
          } = res?.payload?.data;

          setUserToken({
            accessToken: oauthAccessToken,
            expiresAt: oauthExpiresAt,
            user: oauthUser,
            refreshToken: oauthRefreshToken,
          });

          if (shouldRunWelnetAuthentication(oauthUser)) {
            // Token is already set above from oauthAccessToken/oauthUser, so on
            // "not welnet-only" just honor the original redirect_uri instead of
            // handleUpdateLogin's hardcoded /dashboard + redundant setUserToken
            // (which would use this component's URL-parsed accessToken/user —
            // both null in this flow — and throw).
            handleWelnetAuthentication(oauthUser, () => {
              navigate(`${redirect_uri}`);
              setIsLoading(false);
            });
            return;
          }

          navigate(`${redirect_uri}`);
          setIsLoading(false);
        } else {
          toast.error(getMessage || "Invalid Social Login attempt.!");
          navigate("/");
          setIsLoading(false);
        }
      });
      return;
    }

    if (accessToken && user && user.id && redirectURL) {
      setUserToken({ accessToken, expiresAt, user, refreshToken });
      navigate(`${redirectURL}`);
      setIsLoading(false);
      return;
    }

    if (accessToken && user && user.id) {
      completeSocialLogin(user);
      return;
    }

    toast.error(
      getMessage ||
        "Social Login failed. Please try again or use a different sign in method.",
    );
    navigate("/");
    setIsLoading(false);
  }, [
    accessToken,
    expiresAt,
    user,
    redirectURL,
    navigate,
    getMessage,
    key,
    refreshToken,
    handleUpdateLogin,
    handleWelnetAuthentication,
    completeSocialLogin,
    dispatch,
    success,
  ]);

  return isLoading ? (
    <p>{"Loading..."}</p>
  ) : (
    <div className="App">
      <Row className="p-0 m-0">
        <Banner />
      </Row>
    </div>
  );
};

export default SocialCallback;
